'use client';

import React from 'react';
import type { QuestionConfig, AnswerData, ValidationRules } from '../types';

interface MultiSelectQuestionProps {
  config: QuestionConfig;
  value: AnswerData;
  onChange: (value: AnswerData) => void;
  validationRules?: ValidationRules;
  error?: string;
}

export default function MultiSelectQuestion({
  config,
  value,
  onChange,
  validationRules,
  error,
}: MultiSelectQuestionProps) {
  const selectedValues: string[] = value?.values || [];
  const maxSelections = config.maxSelections || validationRules?.maxSelections;
  const showCounter = config.showCounter !== false; // Default true

  const handleToggle = (optionValue: string) => {
    let newValues: string[];

    if (selectedValues.includes(optionValue)) {
      // Remove the value
      newValues = selectedValues.filter((v) => v !== optionValue);
    } else {
      // Add the value if under limit
      if (maxSelections && selectedValues.length >= maxSelections) {
        return; // Don't add if limit reached
      }
      newValues = [...selectedValues, optionValue];
    }

    onChange({ values: newValues });
  };

  const isSelected = (optionValue: string) => {
    return selectedValues.includes(optionValue);
  };

  const isDisabled = (optionValue: string) => {
    return (
      maxSelections &&
      selectedValues.length >= maxSelections &&
      !isSelected(optionValue)
    );
  };

  const isLimitReached = maxSelections && selectedValues.length >= maxSelections;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Counter display */}
      {showCounter && maxSelections && (
        <div
          className={`text-sm font-medium text-center mb-4 transition-colors ${
            isLimitReached ? 'text-[#028D7B]' : 'text-gray-600'
          }`}
        >
          เลือกแล้ว {selectedValues.length}/{maxSelections}
          {isLimitReached && ' ✓'}
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-3">
        {config.options?.map((option) => {
          const selected = isSelected(option.value);
          const disabled = isDisabled(option.value);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={!!disabled}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                selected
                  ? 'bg-[#2A807B] text-white'
                  : disabled
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-200 text-gray-700'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {error && <p className="text-red-600 text-sm mt-4 text-center">{error}</p>}
    </div>
  );
}
